import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { majorContractsService } from '@shared/services/majorContracts';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import LoadingButton from '@shared/components/LoadingButton';

export default function CollectData({ setOverlayView, person }) {
    const { t } = useTranslation();
    const [selectedContract, setSelectedContract] = useState('');
    const [mainContracts, setMainContracts] = useState([]);
    const [joinDate, setJoinDate] = useState('');
    const [globalGroupIdentifier, setGlobalGroupIdentifier] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const id = person?.recordId ?? person?.empId ?? person?.id;
        if (!id) return;

        const fetchMainContracts = async () => {
            try {
                const data = await majorContractsService.getMainContractsByPersonId(id);
                const list = Array.isArray(data) ? data : (data?.content ?? []);
                setMainContracts(list);
                if (list.length && !selectedContract) {
                    setSelectedContract(String(list[0].contractId));
                }
            } catch (error) {
                console.error('Error fetching main contracts:', error);
                setMainContracts([]);
            }
        };

        fetchMainContracts();
    }, [person?.recordId, person?.empId, person?.id]);

    useEffect(() => {
        if (!selectedContract) {
            setJoinDate('');
            setGlobalGroupIdentifier('');
            return;
        }

        const fetchAdditionalAttributes = async () => {
            try {
                const response = await majorContractsService.getEmpAdditionalAttributesByContractId(selectedContract);
                const records = Array.isArray(response) ? response : (response ? [response] : []);

                const ggiRecord = records.find((record) => record?.mnemonic === 'GGI');
                const joinDateRecord = records.find((record) => record?.mnemonic === 'SGEXJOINDATE');

                setGlobalGroupIdentifier(ggiRecord?.value ?? '');
                setJoinDate(joinDateRecord?.value ?? '');
            } catch (error) {
                console.error('Error fetching additional attributes:', error);
                setJoinDate('');
                setGlobalGroupIdentifier('');
            }
        };

        fetchAdditionalAttributes();
    }, [selectedContract]);

    const formatDateForApi = (dateValue) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string') return dateValue;
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSave = async () => {
        const contractId = Number(selectedContract);
        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }

        const formattedDate = formatDateForApi(joinDate);
        const ggiValue = String(globalGroupIdentifier || '').trim();

        if (!formattedDate && !ggiValue) {
            notifyError(t('common.required') || 'Please fill at least one field.');
            return;
        }

        const payload = { contractId };
        if (formattedDate) payload.effectiveDate = formattedDate;
        if (ggiValue) payload.value = ggiValue;

        try {
            setSaving(true);
            await majorContractsService.saveEmpAdditionalAttributes(payload);
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            const response = await majorContractsService.getEmpAdditionalAttributesByContractId(contractId);
            const records = Array.isArray(response) ? response : (response ? [response] : []);
            const ggiRecord = records.find((record) => record?.mnemonic === 'GGI');
            const joinDateRecord = records.find((record) => record?.mnemonic === 'SGEXJOINDATE');
            setGlobalGroupIdentifier(ggiRecord?.value ?? '');
            setJoinDate(joinDateRecord?.value ?? '');
        } catch (error) {
            console.error('Error saving additional attributes:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="main-person-form-container">
            <h3 className="form-subtitle">{t('persons.personContract.collectData') || 'Collect Data'}</h3>
            <div className="flex-column width-50 gap-10 mt-10 form-border-radius" style={{ margin: '0 auto' }}>
                <div className="flex-column width-100 border-radius-10 position-relative">
                    <label className="text-left label-absolute">{t('persons.workExperience.employmentContracts')}</label>
                    <select
                        className="small-select"
                        value={selectedContract}
                        onChange={(e) => setSelectedContract(e.target.value)}
                    >
                        {mainContracts.map(contract => (
                            <option key={contract.contractId} value={contract.contractId}>
                                {contract.name} - {contract.effectiveDate}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="table-responsive custom-table-container width-100 mt-20">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Допълнителни данни</th>
                                <th>Код и Наименование</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Дата на присъединяване в компанията</td>
                                <td>
                                    <CustomDatePicker
                                        name="joinDate"
                                        value={joinDate}
                                        onChange={setJoinDate}
                                        isSmall={true}
                                        isClearable={true}
                                        renderInPortal={true}
                                        popperPlacement="top-start"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>Global Group Identifier</td>
                                <td>
                                    <input
                                        type="text"
                                        className="small-input"
                                        value={globalGroupIdentifier}
                                        onChange={(e) => setGlobalGroupIdentifier(e.target.value)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="form-controls width-50" style={{ margin: '0 auto' }}>
                <LoadingButton
                    type="button"
                    className="form-btn btn-blue"
                    loading={saving}
                    onClick={handleSave}
                >
                    {t('common.save') || 'Save'}
                </LoadingButton>
                <button
                    type="button"
                    className="form-btn btn-red"
                    onClick={() => setOverlayView(null)}
                    title={t('common.exit') || 'Exit'}
                >
                    {t('common.exit') || 'Exit'}
                </button>
            </div>
        </div>
    );
}
